/**
 * usePreprintPermission — per-location pre-print CREATION permission (BLU-717 S3),
 * so a surface can gate its TRIGGER instead of opening a form the backend will
 * reject at submit (DESIGN.md §13.9: gate at the trigger, never open-then-reject).
 *
 * Two independent capabilities, exactly as the backend stores them
 * (`production_settings.garment_preprint_enabled` / `film_preprint_enabled`): a
 * pre-printed garment and a QA'd DTF film are each physical objects that live at
 * ONE location, and neither key inherits the other. Never collapse them.
 *
 * Neutral home (hooks/, not a feature folder) mirroring the backend's neutral
 * `lib/preprint-permission.ts` — consumers sit in `features/inventory`,
 * `features/film-preprint` and `pages/production`, so a feature-local home
 * would make one feature import another for a permission read.
 *
 * POSTURE — deliberately the OPPOSITE of the backend gate, which fails CLOSED:
 * this fails OPEN by returning `enabled: null` ("unknown") whenever we cannot
 * positively read the location's row. `productionSettings.getLocationSettings`
 * is own-locations-only (`.eq('organization_id', callerOrg)`), so at a 3PL
 * shared floor a CLIENT caller's location is simply ABSENT from the list — and
 * `locations.organization_id` is the OWNER there, not "whose floor is this".
 * Reading that absence as "blocked" would hide a legitimate action from a whole
 * org (the owner-org-blindness class: never let a location resolver's null mean
 * "not configured"). Unknown => render the action and let the backend gate rule.
 *
 * The read is also role-gated: `getLocationSettings` is `coordinatorProcedure`,
 * and /inventory is open to Warehouse Staff + Viewer, so firing it for them
 * would 403 on every page load. They get `unknown` (no gate) — and cannot submit
 * a pre-print anyway, since every creation endpoint is coordinator-tier too.
 */
import { useRole } from '@/hooks/use-role';
import { trpc } from '@/lib/trpc';

/** Which physical thing is being pre-printed. Separate keys, no inheritance. */
export type PreprintCapability = 'garment' | 'film';

export interface PreprintPermission {
  /** `true` allowed · `false` blocked · `null` unknown (do NOT gate on null). */
  enabled: boolean | null;
  /** Gate on this, never on `!enabled` — `null` must not read as blocked. */
  blocked: boolean;
  /** The floor's name for the reason copy, when we could read it. */
  locationName: string | null;
  /** True while the permission read is in flight (still `unknown` until it lands). */
  isLoading: boolean;
}

export function usePreprintPermission(
  locationId: string | undefined | null,
  capability: PreprintCapability,
): PreprintPermission {
  const { can } = useRole();
  const canReadLocationSettings = can('production_settings.manage');

  const locationsQuery = trpc.productionSettings.getLocationSettings.useQuery(undefined, {
    // Shared with Production Settings' own sections; a permission flip is a
    // deliberate admin action, so a minute of staleness is the right trade.
    staleTime: 60_000,
    enabled: canReadLocationSettings && !!locationId,
  });

  const row = locationId ? locationsQuery.data?.find((l) => l.id === locationId) : undefined;
  const enabled: boolean | null = row
    ? capability === 'film'
      ? row.filmPreprintEnabled
      : row.preprintEnabled
    : null;

  return {
    enabled,
    blocked: enabled === false,
    locationName: row?.name ?? null,
    isLoading: locationsQuery.isLoading && canReadLocationSettings && !!locationId,
  };
}
